import { cn } from '@commenda/utils';
import React from 'react';

import {
  Tooltip as TooltipBase,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './_base';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  showArrow?: boolean;
  supportingText?: string;
  disabled?: boolean;
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  asChild?: boolean;
  arrowClassName?: string;
}

const Tooltip = ({
  children,
  content,
  className = '',
  side = 'top',
  showArrow = false,
  supportingText = '',
  disabled = false,
  sideOffset = 4,
  align,
  alignOffset,
  asChild = false,
  arrowClassName,
}: TooltipProps) => {
  if (disabled) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={100}>
      <TooltipBase>
        <TooltipTrigger
          className={
            asChild
              ? undefined
              : 'focus:outline-none text-left whitespace-nowrap text-ellipsis overflow-hidden'
          }
          // Only swallow the click for the default (non-asChild) trigger. When asChild,
          // the consumer's element owns its click (e.g. a Popover trigger), and
          // preventDefault here would make Radix bail on `defaultPrevented`.
          onClick={asChild ? undefined : (e) => e.preventDefault()}
          tabIndex={asChild ? undefined : -1}
          asChild={asChild}
        >
          {children}
        </TooltipTrigger>
        {!disabled && (
          <TooltipContent
            className={cn(
              'flex flex-col gap-1 text-white',
              className
            )}
            side={side}
            showArrow={showArrow}
            arrowClassName={arrowClassName}
            sideOffset={sideOffset}
            {...(align !== undefined ? { align } : {})}
            {...(alignOffset !== undefined ? { alignOffset } : {})}
          >
            <span className="normal-case">{content}</span>
            {supportingText.length ? (
              <p className="txt-extra-small-medium">{supportingText}</p>
            ) : null}
          </TooltipContent>
        )}
      </TooltipBase>
    </TooltipProvider>
  );
};

export { Tooltip };
export type { TooltipProps };
